import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import PlaceIcon from "@mui/icons-material/Place";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";

import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import { useAuthContext } from "../../hooks/useAuthContext";
import FormikErrorText from "../../components/ui/FormikErrorText";
import { modalStyleOverflow } from "../../theme/ModalStyle";

/**
 * Admin bulk-actions toolbar for court schedules.
 * Backs four endpoints:
 *  - POST /courtSchedules/bulk-cancel          (QW4)
 *  - POST /courtSchedules/bulk-reassign-coach  (Medium 11)
 *  - POST /courtSchedules/bulk-move-court      (Medium 11)
 *  - POST /courtSchedules/bulk-shift-times     (Medium 11)
 *
 * Supports a dry_run preview before commit on every action that accepts it.
 * Each action returns a per-row result map; we surface conflicts/skips to admin.
 */
function BulkActionsBar({ onSuccess }) {
  const { user } = useAuthContext();
  const access_token = user?.access_token;
  const headers = useMemo(
    () => ({ "content-type": "application/json", authorization: `Bearer ${access_token}` }),
    [access_token]
  );

  const [open, setOpen] = useState(null); // 'cancel' | 'reassign' | 'move' | 'shift' | null
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // option lists
  const [courts, setCourts] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [terms, setTerms] = useState([]);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      Api.get(Urls.GET_COURTS_LIST + "?perPage=200", { headers }).catch(() => ({ data: { data: [] } })),
      Api.get(Urls.GET_COACHES + "?perPage=200", { headers }).catch(() => ({ data: { data: [] } })),
      Api.get(Urls.GET_TERMS_LIST + "?perPage=200", { headers }).catch(() => ({ data: { data: [] } })),
      Api.get(Urls.GET_PROGRAMS + "?perPage=200", { headers }).catch(() => ({ data: { data: [] } })),
    ]).then(([c, co, t, p]) => {
      setCourts(c?.data?.data || []);
      setCoaches(co?.data?.data || []);
      setTerms(t?.data?.data || []);
      setPrograms(p?.data?.data || []);
    });
    setResult(null);
  }, [open, headers]);

  const close = () => {
    setOpen(null);
    setResult(null);
  };

  // ──────── Generic POST runner ────────
  const run = (url, payload) => {
    setSubmitting(true);
    Api.post(url, payload, { headers })
      .then((res) => {
        const body = res?.data?.data || res?.data;
        setResult(body);
        toast.success(payload.dry_run ? "Dry-run complete" : "Bulk action applied");
        if (!payload.dry_run && onSuccess) onSuccess();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Bulk action failed");
      })
      .finally(() => setSubmitting(false));
  };

  // ──────── Render result block ────────
  const ResultBlock = () => {
    if (!result) return null;
    const summary = [];
    if (result.affected_count != null) summary.push(`Affected: ${result.affected_count}`);
    if (result.skipped_count != null) summary.push(`Skipped: ${result.skipped_count}`);
    if (result.conflict_count != null) summary.push(`Conflicts: ${result.conflict_count}`);
    return (
      <Alert severity={result.conflict_count > 0 ? "warning" : "info"} sx={{ mt: 2 }}>
        <Typography fontSize="13px">{summary.join("  ·  ") || "Done."}</Typography>
        {Array.isArray(result.conflicts) && result.conflicts.length > 0 && (
          <Box sx={{ mt: 1, maxHeight: 200, overflow: "auto" }}>
            {result.conflicts.slice(0, 20).map((c, i) => (
              <Typography key={i} fontSize="12px" color="#7B7A86">
                #{c.court_schedule_id}: {c.reason || "conflict"}
              </Typography>
            ))}
          </Box>
        )}
      </Alert>
    );
  };

  // ──────── Common filter fields used by all 4 modals ────────
  const FilterFields = ({ values, setFieldValue }) => (
    <>
      <Box display="flex" gap="8px">
        <Field as={TextField} name="start_date" label="Start date" type="date" InputLabelProps={{ shrink: true }} fullWidth size="small" />
        <Field as={TextField} name="end_date" label="End date" type="date" InputLabelProps={{ shrink: true }} fullWidth size="small" />
      </Box>
      <Field
        as={TextField}
        select
        name="term_id"
        label="Term (optional)"
        fullWidth
        size="small"
        onChange={(e) => setFieldValue("term_id", e.target.value)}
        value={values.term_id || ""}
      >
        <MenuItem value="">All terms</MenuItem>
        {terms.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
      </Field>
      <Field
        as={TextField}
        select
        SelectProps={{ multiple: true }}
        name="court_ids"
        label="Courts (optional)"
        fullWidth
        size="small"
        onChange={(e) => setFieldValue("court_ids", e.target.value)}
        value={values.court_ids || []}
      >
        {courts.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
      </Field>
      <Field
        as={TextField}
        select
        SelectProps={{ multiple: true }}
        name="coach_ids"
        label="Coaches (optional)"
        fullWidth
        size="small"
        onChange={(e) => setFieldValue("coach_ids", e.target.value)}
        value={values.coach_ids || []}
      >
        {coaches.map((c) => <MenuItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</MenuItem>)}
      </Field>
    </>
  );

  return (
    <>
      <Box display="flex" gap="8px" flexWrap="wrap" sx={{ mb: 2 }}>
        <Button startIcon={<EventBusyIcon />} variant="primary_outlined" onClick={() => setOpen("cancel")}>
          Bulk cancel
        </Button>
        <Button startIcon={<SwapVertIcon />} variant="primary_outlined" onClick={() => setOpen("reassign")}>
          Reassign coach
        </Button>
        <Button startIcon={<PlaceIcon />} variant="primary_outlined" onClick={() => setOpen("move")}>
          Move court
        </Button>
        <Button startIcon={<AccessTimeIcon />} variant="primary_outlined" onClick={() => setOpen("shift")}>
          Shift times
        </Button>
      </Box>

      {/* ───── Bulk cancel ───── */}
      <Modal open={open === "cancel"} onClose={close}>
        <Box sx={modalStyleOverflow}>
          <Typography fontSize="18px" fontWeight="600" sx={{ mb: 2 }}>
            Bulk cancel sessions
          </Typography>
          <Formik
            initialValues={{
              start_date: "",
              end_date: "",
              term_id: "",
              court_ids: [],
              coach_ids: [],
              program_duration_id: "",
              session_type: "",
              refund: true,
              dry_run: true,
            }}
            validationSchema={yup.object({
              start_date: yup.string().required("Start date is required"),
              end_date: yup.string().required("End date is required"),
            })}
            onSubmit={(v) => run(Urls.BULK_CANCEL_COURT_SCHEDULES, {
              ...v,
              court_ids: v.court_ids?.length ? v.court_ids : undefined,
              coach_ids: v.coach_ids?.length ? v.coach_ids : undefined,
              term_id: v.term_id || undefined,
              program_duration_id: v.program_duration_id || undefined,
              session_type: v.session_type || undefined,
            })}
          >
            {({ values, setFieldValue }) => (
              <Form>
                <Box display="flex" flexDirection="column" gap="12px">
                  <FilterFields values={values} setFieldValue={setFieldValue} />
                  <Field
                    as={TextField}
                    select
                    name="session_type"
                    label="Session type (optional)"
                    fullWidth
                    size="small"
                    onChange={(e) => setFieldValue("session_type", e.target.value)}
                    value={values.session_type || ""}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="program">Program</MenuItem>
                    <MenuItem value="private_lesson">Private lesson</MenuItem>
                    <MenuItem value="court_booking">Court booking</MenuItem>
                  </Field>
                  <FormControlLabel
                    control={<Switch checked={values.refund} onChange={(e) => setFieldValue("refund", e.target.checked)} />}
                    label="Refund affected players"
                  />
                  <FormControlLabel
                    control={<Switch checked={values.dry_run} onChange={(e) => setFieldValue("dry_run", e.target.checked)} />}
                    label="Dry-run (preview only)"
                  />
                  <ErrorMessage component={FormikErrorText} name="start_date" />
                  <ErrorMessage component={FormikErrorText} name="end_date" />
                  <ResultBlock />
                  <Box display="flex" gap="8px" justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button variant="primary_outlined" onClick={close}>Close</Button>
                    <Button variant="primary_active" type="submit" disabled={submitting}>
                      {submitting ? <CircularProgress size={18} /> : values.dry_run ? "Preview" : "Cancel sessions"}
                    </Button>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>

      {/* ───── Bulk reassign coach ───── */}
      <Modal open={open === "reassign"} onClose={close}>
        <Box sx={modalStyleOverflow}>
          <Typography fontSize="18px" fontWeight="600" sx={{ mb: 2 }}>
            Bulk reassign coach
          </Typography>
          <Formik
            initialValues={{
              start_date: "", end_date: "", term_id: "", court_ids: [], coach_ids: [],
              new_coach_id: "", reason: "", dry_run: true,
            }}
            validationSchema={yup.object({
              start_date: yup.string().required("Required"),
              end_date: yup.string().required("Required"),
              new_coach_id: yup.number().required("Pick a replacement coach"),
            })}
            onSubmit={(v) => run(Urls.BULK_REASSIGN_COACH, {
              ...v,
              court_ids: v.court_ids?.length ? v.court_ids : undefined,
              coach_ids: v.coach_ids?.length ? v.coach_ids : undefined,
              term_id: v.term_id || undefined,
            })}
          >
            {({ values, setFieldValue }) => (
              <Form>
                <Box display="flex" flexDirection="column" gap="12px">
                  <FilterFields values={values} setFieldValue={setFieldValue} />
                  <Field
                    as={TextField}
                    select
                    name="new_coach_id"
                    label="New coach"
                    fullWidth
                    size="small"
                    onChange={(e) => setFieldValue("new_coach_id", e.target.value)}
                    value={values.new_coach_id || ""}
                  >
                    {coaches.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</MenuItem>
                    ))}
                  </Field>
                  <Field as={TextField} name="reason" label="Reason (optional)" fullWidth size="small" multiline rows={2} />
                  <FormControlLabel
                    control={<Switch checked={values.dry_run} onChange={(e) => setFieldValue("dry_run", e.target.checked)} />}
                    label="Dry-run (preview only)"
                  />
                  <ErrorMessage component={FormikErrorText} name="new_coach_id" />
                  <ResultBlock />
                  <Box display="flex" gap="8px" justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button variant="primary_outlined" onClick={close}>Close</Button>
                    <Button variant="primary_active" type="submit" disabled={submitting}>
                      {submitting ? <CircularProgress size={18} /> : values.dry_run ? "Preview" : "Reassign"}
                    </Button>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>

      {/* ───── Bulk move court ───── */}
      <Modal open={open === "move"} onClose={close}>
        <Box sx={modalStyleOverflow}>
          <Typography fontSize="18px" fontWeight="600" sx={{ mb: 2 }}>
            Bulk move to another court
          </Typography>
          <Formik
            initialValues={{
              start_date: "", end_date: "", term_id: "", court_ids: [], coach_ids: [],
              new_court_id: "", dry_run: true,
            }}
            validationSchema={yup.object({
              start_date: yup.string().required("Required"),
              end_date: yup.string().required("Required"),
              new_court_id: yup.number().required("Pick a destination court"),
            })}
            onSubmit={(v) => run(Urls.BULK_MOVE_COURT, {
              ...v,
              court_ids: v.court_ids?.length ? v.court_ids : undefined,
              coach_ids: v.coach_ids?.length ? v.coach_ids : undefined,
              term_id: v.term_id || undefined,
            })}
          >
            {({ values, setFieldValue }) => (
              <Form>
                <Box display="flex" flexDirection="column" gap="12px">
                  <FilterFields values={values} setFieldValue={setFieldValue} />
                  <Field
                    as={TextField}
                    select
                    name="new_court_id"
                    label="New court"
                    fullWidth
                    size="small"
                    onChange={(e) => setFieldValue("new_court_id", e.target.value)}
                    value={values.new_court_id || ""}
                  >
                    {courts.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                  </Field>
                  <FormControlLabel
                    control={<Switch checked={values.dry_run} onChange={(e) => setFieldValue("dry_run", e.target.checked)} />}
                    label="Dry-run (preview only — also runs conflict detection)"
                  />
                  <ErrorMessage component={FormikErrorText} name="new_court_id" />
                  <ResultBlock />
                  <Box display="flex" gap="8px" justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button variant="primary_outlined" onClick={close}>Close</Button>
                    <Button variant="primary_active" type="submit" disabled={submitting}>
                      {submitting ? <CircularProgress size={18} /> : values.dry_run ? "Preview" : "Move"}
                    </Button>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>

      {/* ───── Bulk shift times ───── */}
      <Modal open={open === "shift"} onClose={close}>
        <Box sx={modalStyleOverflow}>
          <Typography fontSize="18px" fontWeight="600" sx={{ mb: 2 }}>
            Bulk shift session times
          </Typography>
          <Formik
            initialValues={{
              start_date: "", end_date: "", term_id: "", court_ids: [], coach_ids: [],
              offset_minutes: 0, dry_run: true,
            }}
            validationSchema={yup.object({
              start_date: yup.string().required("Required"),
              end_date: yup.string().required("Required"),
              offset_minutes: yup.number().integer("Must be an integer").required("Required").notOneOf([0], "Use a non-zero offset"),
            })}
            onSubmit={(v) => run(Urls.BULK_SHIFT_TIMES, {
              ...v,
              court_ids: v.court_ids?.length ? v.court_ids : undefined,
              coach_ids: v.coach_ids?.length ? v.coach_ids : undefined,
              term_id: v.term_id || undefined,
            })}
          >
            {({ values, setFieldValue }) => (
              <Form>
                <Box display="flex" flexDirection="column" gap="12px">
                  <FilterFields values={values} setFieldValue={setFieldValue} />
                  <Field
                    as={TextField}
                    name="offset_minutes"
                    type="number"
                    label="Offset (minutes — negative shifts earlier)"
                    fullWidth
                    size="small"
                  />
                  <Box display="flex" gap="6px">
                    {[-60, -30, -15, +15, +30, +60].map((m) => (
                      <Chip
                        key={m}
                        label={`${m > 0 ? "+" : ""}${m}m`}
                        size="small"
                        onClick={() => setFieldValue("offset_minutes", m)}
                      />
                    ))}
                  </Box>
                  <FormControlLabel
                    control={<Switch checked={values.dry_run} onChange={(e) => setFieldValue("dry_run", e.target.checked)} />}
                    label="Dry-run (preview only — also runs conflict detection)"
                  />
                  <ErrorMessage component={FormikErrorText} name="offset_minutes" />
                  <ResultBlock />
                  <Box display="flex" gap="8px" justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button variant="primary_outlined" onClick={close}>Close</Button>
                    <Button variant="primary_active" type="submit" disabled={submitting}>
                      {submitting ? <CircularProgress size={18} /> : values.dry_run ? "Preview" : "Apply shift"}
                    </Button>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
    </>
  );
}

export default BulkActionsBar;
